/**
 * "What certificates are due next month?" — the renewals in a date range, as a
 * count per week, a chip per type and the first few customers
 * (docs/intelligent-chat-answers-plan.md, "Certificates due").
 *
 * 🚨 Built from the server's own read (`certs-due-data.ts`), never from
 * anything the model wrote. Every label on it — the type, the due date, the
 * week ranges — is decided here or in that read, so the panel, the Mucka Bar
 * and a reply redrawn next month all say the same thing.
 *
 * 🚨 The block SENDS NOTHING. Its one action asks Mucka, in the owner's words,
 * to go through the reminder cards that already exist (`cert_renewal` /
 * `service_reminder`), each of which is approved on its own with its own
 * read-back. A bulk "Send 9 reminders" from the block would be a mailshot with
 * no per-customer approval, which is exactly what `spokenApprovalPreview`
 * refuses on a card naming more than three customers.
 *
 * What it deliberately does not show, because nothing stores it: whether a
 * tenant has been given notice, or whether a reminder would go by text or by
 * email. "Tenant" means a current tenant contact is on the property, nothing
 * more.
 *
 * Pure and directive-free: the route, both chat surfaces and the oversight
 * grader import it (anti-pattern #18).
 */

/** Where a row came from. Both are renewals; they live in two tables. */
export type CertsDueSource = 'certificate' | 'service_reminder';

/**
 * What has already happened about a row, as far as Mucka can see.
 *
 * `booked`: the reminder is pencilled or booked in. `reminded`: a reminder has
 * gone (the reminder's own status, or an approved renewal card).
 * `card_waiting`: a reminder card is on the Actions list, not yet decided.
 * Null: none of those.
 */
export type CertsDueState = 'booked' | 'reminded' | 'card_waiting' | null;

/** One renewal, as the server read it — the builder's input. */
export interface CertsDueItem {
  /** `gas_certificates.id` or `service_reminders.id`. */
  recordId: string;
  source: CertsDueSource;
  /** The customer's name, built server-side from the client record. */
  who: string;
  postcode: string | null;
  /**
   * The type as a person reads it. For a certificate this is
   * `certReminderTitle(cert_type)` — the SAME classification the renewal card
   * uses (#37), never re-derived. For a reminder with no certificate behind it,
   * the reminder's own title, verbatim: that is the owner's word for it.
   */
  type: string;
  /** YYYY-MM-DD: `next_inspection_date` or `due_date`. */
  dueDate: string;
  /** A current tenant contact is on the property (`property_contacts`). */
  tenant: boolean;
  state: CertsDueState;
}

export interface CertsDueRow {
  /** Unique across both sources. */
  id: string;
  source: CertsDueSource;
  who: string;
  postcode: string | null;
  type: string;
  dueDate: string;
  /** "3 Nov", built here so every surface says the same. */
  dueLabel: string;
  /** Due before today (London). */
  overdue: boolean;
  tenant: boolean;
  state: CertsDueState;
  /** Where the row opens. */
  path: string;
}

/** One week tile: a seven-day span of the range, the last one taking the remainder. */
export interface CertsDueTile {
  /** "1–7 Nov", or "29 Oct–4 Nov" across a month end. */
  label: string;
  from: string;
  to: string;
}

/** A view of the list: everything, or one type. */
export interface CertsDueView {
  /** `all`, or the type label itself. */
  key: string;
  label: string;
  total: number;
  /** One count per tile, same order as `tiles`. Empty when there are no tiles. */
  weeks: number[];
  /** The earliest few, by due date. `total - rows.length` are counted, never dropped silently. */
  rows: CertsDueRow[];
}

export interface CertsDueLink {
  label: string;
  path: string;
}

export interface CertsDueBlock {
  kind: 'certs-due';
  from: string;
  to: string;
  /** "in November", "between 3 Nov and 16 Nov", "from 1 Nov". */
  rangeLabel: string;
  /** Up to four. Empty for a range of a week or less, or one too long to tile. */
  tiles: CertsDueTile[];
  all: CertsDueView;
  /** One per type, largest first, at most `CERTS_DUE_MAX_CHIPS`. Empty when there is only one type. */
  types: CertsDueView[];
  /** Renewals in the range with a reminder card waiting on the Actions list. */
  cardsWaiting: number;
  /** Whether "Ask Mucka to send reminders" is offered: a card is waiting AND the seat may approve one. */
  canAsk: boolean;
  /** Plain sentences about what this seat cannot see, or what was not counted. */
  notes: string[];
  /** Go-rows onto the pages that hold these, only the ones this seat can open. */
  links: CertsDueLink[];
}

/** Rows shown per view. The rest are counted. */
export const CERTS_DUE_MAX_ROWS = 5;
/** Type chips beyond "All". A type past these is still in All. */
export const CERTS_DUE_MAX_CHIPS = 4;
/** Week tiles at most; the last absorbs whatever is left of the range. */
export const CERTS_DUE_MAX_TILES = 4;
/** A range longer than this is not tiled (a fifth "week" would be a fortnight). */
const TILE_RANGE_MAX_DAYS = 35;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Open ends from `resolveQueryRange`: `new Date(0)` and 31 Dec 2999. */
function isOpenStart(iso: string): boolean { return Number(iso.slice(0, 4)) < 1980; }
function isOpenEnd(iso: string): boolean { return Number(iso.slice(0, 4)) > 2900; }

function dayNum(iso: string): number {
  return Math.round(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / 86_400_000);
}
function isoOf(day: number): string {
  return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

/**
 * "3 Nov". Built by hand, not `toLocaleDateString`: Node and WebKit spell
 * September differently and a redrawn reply must read the same on both (#41).
 */
export function shortDate(iso: string): string {
  return `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;
}

function lastDayOfMonth(iso: string): string {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** How the range reads in a sentence. `todayIso` decides whether the year is worth saying. */
export function certsDueRangeLabel(from: string, to: string, todayIso: string): string {
  const openStart = isOpenStart(from);
  const openEnd = isOpenEnd(to);
  if (openStart && openEnd) return 'on file';
  if (openEnd) return `from ${shortDate(from)}`;
  if (openStart) return `up to ${shortDate(to)}`;
  if (from === to) return `on ${shortDate(from)}`;
  if (from.slice(8, 10) === '01' && to === lastDayOfMonth(from)) {
    const month = MONTHS_LONG[Number(from.slice(5, 7)) - 1];
    return from.slice(0, 4) === todayIso.slice(0, 4) ? `in ${month}` : `in ${month} ${from.slice(0, 4)}`;
  }
  return `between ${shortDate(from)} and ${shortDate(to)}`;
}

/** Seven-day spans from the start of the range, at most four, the last running to the end. */
export function certsDueTiles(from: string, to: string): CertsDueTile[] {
  if (isOpenStart(from) || isOpenEnd(to)) return [];
  const start = dayNum(from);
  const end = dayNum(to);
  const days = end - start + 1;
  if (days <= 7 || days > TILE_RANGE_MAX_DAYS) return [];
  const count = Math.min(CERTS_DUE_MAX_TILES, Math.ceil(days / 7));
  const tiles: CertsDueTile[] = [];
  for (let i = 0; i < count; i++) {
    const a = start + i * 7;
    const b = i === count - 1 ? end : a + 6;
    const fromIso = isoOf(a);
    const toIso = isoOf(b);
    const sameMonth = fromIso.slice(0, 7) === toIso.slice(0, 7);
    const label = sameMonth
      ? `${Number(fromIso.slice(8, 10))}–${shortDate(toIso)}`
      : `${shortDate(fromIso)}–${shortDate(toIso)}`;
    tiles.push({ label, from: fromIso, to: toIso });
  }
  return tiles;
}

function tileIndex(tiles: readonly CertsDueTile[], dueDate: string): number {
  if (!tiles.length) return -1;
  for (let i = 0; i < tiles.length; i++) if (dueDate <= tiles[i].to) return i;
  return tiles.length - 1;
}

/**
 * Is a date range on a certificates lookup a question about what is DUE?
 *
 * The range reads against the next inspection date only when it reaches today
 * or later — "next month", "this week". A range wholly in the past ("what did
 * we do last week?") is about certificates ISSUED, and must not be answered
 * with renewals.
 */
export function isDueRangeQuestion(from: string, to: string, todayIso: string): boolean {
  return to >= todayIso;
}

function rowFor(item: CertsDueItem, todayIso: string): CertsDueRow {
  return {
    id: `${item.source === 'certificate' ? 'cert' : 'reminder'}:${item.recordId}`,
    source: item.source,
    who: item.who,
    postcode: item.postcode,
    type: item.type,
    dueDate: item.dueDate,
    dueLabel: shortDate(item.dueDate),
    overdue: item.dueDate < todayIso,
    tenant: item.tenant,
    state: item.state,
    // A certificate has its own page. A reminder does not, so it opens the
    // ledger it sits in.
    path: item.source === 'certificate' ? `/gas-certs/${item.recordId}` : '/service-reminders',
  };
}

function viewOf(key: string, label: string, rows: readonly CertsDueRow[], tiles: readonly CertsDueTile[]): CertsDueView {
  const weeks = tiles.map(() => 0);
  for (const r of rows) {
    const at = tileIndex(tiles, r.dueDate);
    if (at >= 0) weeks[at] += 1;
  }
  return { key, label, total: rows.length, weeks, rows: rows.slice(0, CERTS_DUE_MAX_ROWS) };
}

export interface CertsDueBuildOptions {
  /** YYYY-MM-DD, London; open ends as `resolveQueryRange` gives them. */
  from: string;
  to: string;
  todayIso: string;
  /** The seat may approve an action card (`approve_actions`). */
  canApprove: boolean;
  notes?: string[];
  links?: CertsDueLink[];
}

/**
 * The block, or null when there is nothing to draw. An empty range gets no
 * component: the sentence says it, and names the dates it covered.
 */
export function certsDueBlock(items: readonly CertsDueItem[], opts: CertsDueBuildOptions): CertsDueBlock | null {
  if (!items.length) return null;
  const rows = items
    .map(i => rowFor(i, opts.todayIso))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.who.localeCompare(b.who) || a.id.localeCompare(b.id));
  const tiles = certsDueTiles(opts.from, opts.to);

  const byType = new Map<string, CertsDueRow[]>();
  for (const r of rows) {
    const list = byType.get(r.type);
    if (list) list.push(r);
    else byType.set(r.type, [r]);
  }
  const types = byType.size < 2
    ? []
    : [...byType.entries()]
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .slice(0, CERTS_DUE_MAX_CHIPS)
      .map(([type, list]) => viewOf(type, type, list, tiles));

  const cardsWaiting = rows.filter(r => r.state === 'card_waiting').length;
  return {
    kind: 'certs-due',
    from: opts.from,
    to: opts.to,
    rangeLabel: certsDueRangeLabel(opts.from, opts.to, opts.todayIso),
    tiles,
    all: viewOf('all', 'All', rows, tiles),
    types,
    cardsWaiting,
    canAsk: opts.canApprove && cardsWaiting > 0,
    notes: opts.notes ?? [],
    links: opts.links ?? [],
  };
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * One line for the oversight transcript: counts and states, nothing a customer
 * could be identified by.
 */
export function summariseCertsDueBlock(block: CertsDueBlock): string {
  const all = block.all.rows;
  const parts = [`${block.all.total} due ${block.rangeLabel}`];
  const typeCount = block.types.length || 1;
  parts.push(plural(typeCount, 'type'));
  if (block.tiles.length) parts.push(`${block.tiles.length} week tiles`);
  const tenants = all.filter(r => r.tenant).length;
  if (tenants) parts.push(`${tenants} of the first ${all.length} with a tenant`);
  const overdue = all.filter(r => r.overdue).length;
  if (overdue) parts.push(`${overdue} of the first ${all.length} overdue`);
  if (block.cardsWaiting) parts.push(plural(block.cardsWaiting, 'reminder card') + ' waiting');
  parts.push(block.canAsk ? 'Ask Mucka to send reminders offered' : 'no send offered');
  if (block.notes.length) parts.push('permission-limited');
  return `[certs-due: ${parts.join(', ')}]`;
}

/**
 * What "Ask Mucka to send reminders" says, in the owner's own voice. Nothing is
 * sent by the tap: Mucka finds the waiting cards and reads each one back.
 */
export function certsDueAskRequest(block: Pick<CertsDueBlock, 'rangeLabel'>): string {
  return `Go through the reminder cards waiting for what's due ${block.rangeLabel}, one at a time, so I can send them`;
}
